import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

// Import hero screenshots
import shineToSignHero from "@/assets/portfolio-shine-to-sign.jpg";
import riskResolveHero from "@/assets/portfolio-risk-resolve.jpg";
import renovoBoostHero from "@/assets/portfolio-renovo-boost.jpg";
import sparkLeadHero from "@/assets/portfolio-spark-lead.jpg";
import travelFlashHero from "@/assets/portfolio-travel-flash.jpg";

interface PortfolioCarouselProps {
  showFullHero?: boolean;
  heroMode?: boolean;
}

const PortfolioCarousel = ({ showFullHero = false, heroMode = false }: PortfolioCarouselProps) => {
  const websites = [
    {
      name: "Shine to Sign",
      url: "https://shine-to-sign.lovable.app/",
      description: "Elite detailing booking system for auto professionals",
      image: shineToSignHero
    },
    {
      name: "Renovo Boost",
      url: "https://renovo-boost.lovable.app/",
      description: "Lead generation for kitchen, bathroom & home remodeling",
      image: renovoBoostHero
    },
    {
      name: "Spark Lead Systems",
      url: "https://spark-lead-systems.lovable.app/",
      description: "Done-for-you AI growth systems and automation",
      image: sparkLeadHero
    },
    {
      name: "Travel Flash Offer",
      url: "https://travel-flash-offer.lovable.app/",
      description: "Exclusive deals on breathtaking destinations worldwide",
      image: travelFlashHero
    }
  ];

  return (
    <section className={heroMode ? "py-6" : "py-10 md:py-14 bg-background"}>
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {!heroMode && (
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Websites We've Built for <span className="text-secondary">Lead Generation</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Every website is designed with one goal: convert visitors into high-quality leads for your business
              </p>
            </div>
          )}

          <Carousel className={`w-full ${heroMode ? 'max-w-6xl' : 'max-w-5xl'} mx-auto`}>
            <CarouselContent>
              {websites.map((site, index) => (
                <CarouselItem key={index} className={heroMode ? "md:basis-1/2 lg:basis-1/2" : "md:basis-1/2 lg:basis-1/3"}>
                  <div className="p-1">
                    <Card className="border-2 border-secondary/20 hover:border-secondary transition-all duration-300 hover:shadow-luxury group cursor-pointer overflow-hidden">
                      <a href={site.url} target="_blank" rel="noopener noreferrer" className="block">
                        <CardContent className="p-0">
                          <div className={`${heroMode ? 'aspect-video' : 'aspect-video'} bg-muted overflow-hidden relative`}>
                            <img 
                              src={site.image} 
                              alt={`${site.name} website preview`}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-8">
                              <div className="text-white text-center">
                                <ExternalLink className="w-10 h-10 mx-auto mb-2" />
                                <p className="font-semibold text-lg">View Live Site</p>
                              </div>
                            </div>
                          </div>
                          {!heroMode && (
                            <div className="p-6">
                              <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-secondary transition-colors">{site.name}</h3>
                              <p className="text-muted-foreground text-sm">{site.description}</p>
                            </div>
                          )}
                        </CardContent>
                      </a>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className={heroMode ? "left-2" : ""} />
            <CarouselNext className={heroMode ? "right-2" : ""} />
          </Carousel>

          {!heroMode && (
            <div className="text-center mt-12">
              <p className="text-lg text-muted-foreground mb-6">
                Want a custom lead generation website like these?
              </p>
              <Button variant="premium" size="lg" asChild>
                <a href="/do-you-qualify">
                  Schedule Strategic Review
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PortfolioCarousel;
